/**
 * Générateur — Lire l'abscisse d'un nombre relatif sur une droite graduée et
 * placer un nombre relatif d'abscisse donnée.
 * Attendu ID : 706 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 2)
 *
 * Plan de cartes :
 *
 *   Carte « lire » — Ex.4 Donne l'abscisse de chaque point
 *     Élément numberline NATIF (affichage, showValue masqué), 4 régimes de
 *     finesse fidèles au doc : entiers, dixièmes (autour de 0 et grands
 *     négatifs type -9,9), centièmes autour de 0. Formats : texte_trou_drag
 *     (étiquettes signées — zéro frappe), qcm_unique, texte (saisie, point
 *     NÉGATIF uniquement : pour un positif « 2 » et « +2 » seraient tous deux
 *     justes), tableau_vf (abscisses vraies/fausses, signes).
 *
 *   Carte « placer » — Ex.5 et Ex.6 Place les points donnés
 *     Format INTERACTIF placer_points (snap sur graduation), un format par
 *     régime de finesse = la progression a/b/c/d du doc :
 *       placer_demis      : pas 0,5 autour de 0 (Ex.5a : -3 ; +2,5 ; -0,5)
 *       placer_dixiemes   : pas 0,1 — zone négative (Ex.5b) OU grands
 *                           négatifs type -9,9/-10,3 (Ex.6b)
 *       placer_centiemes  : pas 0,01 autour de 0 (Ex.5c : -0,12 ; 0,06)
 *       placer_milliemes  : pas 0,005 (Ex.6d ADAPTÉ : valeurs recalées sur la
 *                           grille de snap — -2,568 devient -2,565 : le
 *                           composant ne peut snapper qu'aux graduations)
 *     Règles §9 respectées par construction : valeurs = représentation
 *     canonique du snap (virgule FR, sans zéros finaux, sans « + »),
 *     TOUJOURS sur une graduation, ≤ 100 graduations.
 *
 * Arithmétique interne en MILLIÈMES entiers (zéro flottant).
 * Cadrage : « décimaux relatifs simples » — valeurs lisibles, repères entiers.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, basePayload,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 706;

const L_LIRE = 'Lire l\'abscisse d\'un point';
const L_PLACER = 'Placer des points sur la droite graduée';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// millièmes → écriture française canonique ("-3,6", "0,06", "-2,545", "5")
const frM = (m) => {
    const s = m < 0 ? '-' : '';
    const a = Math.abs(m);
    const ent = Math.floor(a / 1000);
    const frac = String(a % 1000).padStart(3, '0').replace(/0+$/, '');
    return s + (frac ? `${ent},${frac}` : String(ent));
};
// millièmes → écriture SIGNÉE pour l'affichage/étiquettes ("+2,5", "-3", "0")
const signeM = (m) => (m === 0 ? '0' : m < 0 ? frM(m) : `+${frM(m)}`);

const LETTRES_PTS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K'];

// ─── Régimes de graduation (tout en millièmes) ───────────────
// start/end/pas : bornes et pas ; reperes : graduations étiquetées.

const REGIMES_LIRE = [
    () => ({ nom: 'entiers', start: -6000, end: 6000, pas: 1000, reperes: [0, 1] }),
    () => { // dixièmes autour d'un entier négatif (Ex.4d / Ex.5b)
        const c = -randInt(2, 8) * 1000;
        return { nom: 'dixiemes', start: c - 1500, end: c + 1500, pas: 100, reperes: [c / 1000] };
    },
    () => { // dixièmes, grands négatifs (Ex.6b : autour de -10)
        const c = -randInt(9, 15) * 1000;
        return { nom: 'dixiemes_grands', start: c - 600, end: c + 600, pas: 100, reperes: [c / 1000] };
    },
    () => ({ nom: 'centiemes', start: -250, end: 100, pas: 10, reperes: [-0.2, -0.1, 0] }),
];

/** Tire n abscisses DISTINCTES sur la grille (jamais les bornes, jamais un repère). */
const tirerAbscisses = (reg, n, filtre = () => true) => {
    const grille = [];
    for (let m = reg.start + reg.pas; m <= reg.end - reg.pas; m += reg.pas) {
        if (!reg.reperes.includes(m / 1000) && filtre(m)) grille.push(m);
    }
    return shuffle(grille).slice(0, n);
};

const elementDroite = (reg, valeursM, lettres) => ({
    version: 1,
    elements: [{
        type: 'numberline',
        config: {
            start: reg.start / 1000, end: reg.end / 1000, step: reg.pas / 1000,
            reperes: reg.reperes,
            points: valeursM.map((m, i) => ({ value: m / 1000, label: lettres[i], showValue: false })),
        },
    }],
});

// ═════════ Carte 1 — Lire l'abscisse (Ex.4) ═════════

const genLireDrag = () => {
    const reg = randomChoice(REGIMES_LIRE)();
    const vals = tirerAbscisses(reg, 3);
    const lettres = shuffle(LETTRES_PTS).slice(0, 3);
    const phrase = vals.map((m, i) => `${lettres[i]}( {{${signeM(m)}}} )`).join(' ; ') + '.';
    const candidats = vals.flatMap(m => [signeM(-m), signeM(m + reg.pas), signeM(m - reg.pas)]);
    const payload = makeTexteTrou(phrase,
        '<p><b>Donne l\'abscisse de chaque point.</b></p>',
        {
            ...opts(L_LIRE), mode: 'drag',
            distracteurs: filtrerDistracteurs(phrase, candidats).slice(0, 4),
            elements_additionnels: elementDroite(reg, vals, lettres),
        });
    payload._meta.params = { regime: reg.nom, vals };
    return payload;
};

const genLireQcm = () => {
    const reg = randomChoice(REGIMES_LIRE)();
    const vals = tirerAbscisses(reg, 3);
    const lettres = shuffle(LETTRES_PTS).slice(0, 3);
    const cible = randInt(0, 2);
    const m = vals[cible];
    const correct = signeM(m);
    const candidats = [signeM(-m), signeM(m + reg.pas), signeM(m - reg.pas), signeM(m + 2 * reg.pas)];
    const distracteurs = [];
    candidats.forEach(c => {
        if (distracteurs.length < 3 && c !== correct && !distracteurs.includes(c)) distracteurs.push(c);
    });
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p><b>Quelle est l'abscisse du point ${lettres[cible]} ?</b></p>`,
        { ...opts(L_LIRE), elements_additionnels: elementDroite(reg, vals, lettres) });
    payload._meta.params = { regime: reg.nom, m };
    return payload;
};

const genLireSaisie = () => {
    const reg = randomChoice(REGIMES_LIRE)();
    const vals = tirerAbscisses(reg, 3, (m) => true);
    // le point interrogé est NÉGATIF (un positif accepterait « 2 » ET « +2 »)
    let negatifs = vals.filter(m => m < 0);
    if (negatifs.length === 0) {
        vals[0] = -Math.abs(vals[0]) || -reg.pas; // repli : force une valeur négative de la grille
        negatifs = [vals[0]];
    }
    const m = randomChoice(negatifs);
    const lettres = shuffle(LETTRES_PTS).slice(0, 3);
    const cible = lettres[vals.indexOf(m)];
    const payload = makeSaisie(frM(m),
        `<p><b>Quelle est l'abscisse du point ${cible} ?</b> Écris-la avec son signe (ex. : -3 ou -2,6).</p>`,
        { ...opts(L_LIRE), elements_additionnels: elementDroite(reg, vals, lettres) });
    payload._meta.params = { regime: reg.nom, m };
    return payload;
};

const genLireVf = () => {
    const reg = randomChoice(REGIMES_LIRE)();
    const vals = tirerAbscisses(reg, 4);
    const lettres = shuffle(LETTRES_PTS).slice(0, 4);
    const abs1Juste = randomChoice([true, false]);
    const abs2Juste = randomChoice([true, false]);
    const signePoint = vals[2] < 0;
    const lignes = shuffle([
        [`L'abscisse du point ${lettres[0]} est ${signeM(abs1Juste ? vals[0] : vals[0] + reg.pas)}.`, abs1Juste ? 0 : 1],
        [`L'abscisse du point ${lettres[1]} est ${signeM(abs2Juste ? vals[1] : -vals[1])}.`, abs2Juste ? 0 : 1],
        [`Le point ${lettres[2]} a une abscisse ${signePoint ? 'négative' : 'positive'}.`, 0],
        [`Le point ${lettres[3]} a une abscisse ${vals[3] < 0 ? 'positive' : 'négative'}.`, 1],
    ]);
    const payload = makeVraiFaux(
        lignes.map(l => l[0]),
        lignes.map(l => l[1]),
        '<p><b>Observe la droite graduée. Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        { ...opts(L_LIRE), elements_additionnels: elementDroite(reg, vals, lettres) });
    payload._meta.params = { regime: reg.nom, vals };
    return payload;
};

// ═════════ Carte 2 — Placer des points (Ex.5 / Ex.6) ═════════

const REGIMES_PLACER = {
    demis: () => ({ start: -6000, end: 6000, pas: 500, reperes: [0, 1] }),
    dixiemes: () => {
        if (randomChoice([true, false])) { // zone négative autour de 0 (Ex.5b)
            const c = -randInt(2, 8) * 1000;
            return { start: c - 1500, end: c + 1500, pas: 100, reperes: [c / 1000] };
        }
        const c = -randInt(9, 15) * 1000;  // grands négatifs (Ex.6b)
        return { start: c - 600, end: c + 600, pas: 100, reperes: [c / 1000] };
    },
    centiemes: () => ({ start: -250, end: 100, pas: 10, reperes: [-0.2, -0.1, 0] }),
    milliemes: () => { // Ex.6d adapté : pas 0,005, valeurs SUR la grille de snap
        const c = -(randInt(24, 29) * 100 + randomChoice([0, 50])); // ex. -2 600 ou -2 650
        return { start: c - 75, end: c + 75, pas: 5, reperes: [c / 1000] };
    },
};

const genPlacer = (regime) => () => {
    const reg = REGIMES_PLACER[regime]();
    // ≥ 1 valeur négative garantie (le cœur de l'attendu)
    let vals = tirerAbscisses(reg, 3);
    if (!vals.some(m => m < 0)) {
        const negatifs = tirerAbscisses(reg, 1, (m) => m < 0);
        if (negatifs.length > 0) vals[0] = negatifs[0];
    }
    vals = shuffle(vals);
    const lettres = shuffle(LETTRES_PTS).slice(0, vals.length);
    const bonne = {};
    vals.forEach((m, i) => { bonne[lettres[i]] = frM(m); });
    const consigne = vals.map((m, i) => `${lettres[i]}(${signeM(m)})`).join(' ; ');
    const payload = basePayload('association',
        `<p><b>Place les points ${consigne} sur la droite graduée.</b></p>`,
        {
            subType: 'placer_points',
            numberline: { start: reg.start / 1000, end: reg.end / 1000, step: reg.pas / 1000, reperes: reg.reperes },
            pointsToPlace: lettres,
            bonne_reponse: JSON.stringify(bonne),
        },
        opts(L_PLACER));
    payload._meta.params = { regime, vals };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    lire_texte_trou_drag: genLireDrag,
    lire_qcm_unique: genLireQcm,
    lire_texte: genLireSaisie,
    lire_vf: genLireVf,
    placer_demis_placer_points: genPlacer('demis'),
    placer_dixiemes_placer_points: genPlacer('dixiemes'),
    placer_centiemes_placer_points: genPlacer('centiemes'),
    placer_milliemes_placer_points: genPlacer('milliemes'),
};

const FORMAT_LABELS = {
    texte_trou_drag: 'Drag', qcm_unique: 'QCM', texte: 'Saisie', vf: 'Vrai/Faux',
    demis_placer_points: 'Placer (0,5)', dixiemes_placer_points: 'Placer (0,1)',
    centiemes_placer_points: 'Placer (0,01)', milliemes_placer_points: 'Placer (0,005)',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'lire', label: L_LIRE, formatsList: buildFormats('lire') },
    { id: 'placer', label: L_PLACER, formatsList: buildFormats('placer') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
